import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, IconButton, Stack, Table, TableBody, TableCell, TableRow, TextField, Typography,
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

// Таблица спряжения целиком: io…loro по порядку, как учили «от зубов» у Петрова.
// Под каждой строкой — что это значит по-русски; после проверки — где ошибка.
export default function TableCard({ card, values, onChange, result, onSubmit, firstRef, onSpeak, t }) {
  const refs = useRef([]);
  const rows = card.rows || [];
  // новая таблица: сначала смотрим и читаем вслух всю таблицу, потом закрываем и пишем по памяти
  const [studying, setStudying] = useState(Boolean(card.is_new && !result));
  useEffect(() => {
    if (!studying && !result) setTimeout(() => refs.current[0]?.focus(), 50);
  }, [studying, result]);

  if (studying) {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">{t('study.tableLearn')}</Typography>
        <Stack direction="row" sx={{ alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>{card.infinitive} — {card.gloss}</Typography>
          {onSpeak && (
            // вся таблица подряд: «io sarei, tu saresti, …» — слушать и повторять
            <IconButton size="small" sx={{ ml: 1 }} aria-label={t('study.speak')}
              onClick={() => onSpeak(rows.map((r) => `${r.label.split('/')[0]} ${r.expected[0]}`).join(', '))}>
              <VolumeUpIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        <Table size="small" sx={{ mt: 1 }}>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.person}>
                <TableCell sx={{ py: 0.75, width: 64 }}>{row.label}</TableCell>
                <TableCell sx={{ py: 0.75 }}>
                  <Typography sx={{ fontWeight: 600 }}>{row.display}</Typography>
                  {row.native && <Typography variant="caption" color="text.secondary">{row.native}</Typography>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={() => setStudying(false)}>
          {t('study.tableMemorized')}
        </Button>
      </Box>
    );
  }

  const onKeyDown = (k) => (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (k < rows.length - 1) refs.current[k + 1]?.focus();
    else onSubmit();
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary">{t('study.tableTitle')}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 600 }}>{card.infinitive} — {card.gloss}</Typography>
      {card.aux && (
        <Typography variant="body2" color="text.secondary">💡 {t('study.tableAux', { aux: card.aux })}</Typography>
      )}
      <Stack spacing={1} sx={{ mt: 1.5 }}>
        {rows.map((row, k) => {
          const res = result?.rows?.[k];
          const ok = res && res.verdict !== 'wrong';
          return (
            <Box key={row.person}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography sx={{ width: 64, flexShrink: 0 }}>{row.label}</Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={values[k] || ''}
                  onChange={(e) => onChange(k, e.target.value)}
                  onKeyDown={onKeyDown(k)}
                  inputRef={(el) => { refs.current[k] = el; if (k === 0 && firstRef) firstRef.current = el; }}
                  disabled={!!result}
                  error={res ? !ok : false}
                  color={ok ? 'success' : 'primary'}
                  focused={ok ? true : undefined}
                  autoComplete="off"
                  inputProps={{
                    autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false, lang: 'it',
                    enterKeyHint: k < rows.length - 1 ? 'next' : 'done',
                  }}
                />
              </Stack>
              {row.native && !result && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: '72px' }}>{row.native}</Typography>
              )}
              {res && res.verdict !== 'exact' && (
                <Typography variant="caption" color={ok ? 'warning.main' : 'error'} sx={{ ml: '72px', display: 'block' }}>
                  → {row.label} {res.expected}
                </Typography>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
